import type { DayKey, DayRange } from './day';
import { addDays, inRange } from './day';
import { fmtQty } from './money';
import type { PayMode, Product, StockReason } from './types';

/**
 * The shop's ledger: an append-only, day-stamped record of what happened.
 *
 * This exists because the app otherwise stores only aggregates — `salesToday`,
 * `Product.remaining`, `Customer.balance` — with no history behind them, and you
 * cannot answer "stock on 12 August" or "profit last week" from an aggregate.
 *
 * It is deliberately separate from the outbox. The outbox is a delivery queue:
 * it prunes once the server confirms an event, and a rejected event becomes a
 * `conflict`. Reports reading it would be destroyed by a successful sync and
 * would silently rewrite last month's profit when a conflict landed. The journal
 * also carries fields the event payloads deliberately do not: the local business
 * day, base-unit quantities, and cost of goods snapshotted at sale time.
 *
 * Mirror as ledger tables in the back office — relational there rather than one
 * array, because Postgres can join and IndexedDB cannot.
 */

export type JournalKind = 'sale' | 'payment' | 'purchase' | 'expense' | 'stock' | 'dayclose';

interface JournalBase {
  /** Same id as the originating event, so the two can be reconciled. */
  id: string;
  at: string;
  day: DayKey;
  kind: JournalKind;
}

export interface JournalLine {
  productId: string;
  name: string;
  /** The unit as sold or received — what a human reads. */
  unit: string;
  qty: number;
  /** The same quantity in the product's base unit — what moves stock. */
  qtyBase: number;
  /** LAK. Revenue on a sale, spend on a purchase. */
  amount: number;
  /**
   * Cost in LAK, snapshotted. Absent when the product had no cost.
   *
   * Per *base* unit on a sale — it is the product's moving average at that
   * moment — and per *received* unit on a purchase, which is what the shop was
   * invoiced and what `sell`/`receive` each multiply to reach their total.
   * `_reaverage_cost` divides the purchase figure by `perBase` before blending
   * it in. Anything reading this has to know which kind of entry it came off.
   */
  unitCost?: number;
}

export interface SaleJournal extends JournalBase {
  kind: 'sale';
  total: number;
  payMode: PayMode;
  customerId?: string;
  customerName?: string;
  invoiceNo: string;
  /** Cost of the goods sold, snapshotted at sale time. */
  cogs: number;
  /** The portion of `total` whose products had a cost recorded. */
  costedRevenue: number;
  lines: JournalLine[];
}

export interface PaymentJournal extends JournalBase {
  kind: 'payment';
  customerId: string;
  customerName?: string;
  amount: number;
}

export interface PurchaseJournal extends JournalBase {
  kind: 'purchase';
  supplier: string;
  total: number;
  paid: boolean;
  cash: boolean;
  lines: JournalLine[];
}

export interface ExpenseJournal extends JournalBase {
  kind: 'expense';
  category: string;
  note?: string;
  /** Negative on a reversal, so a period simply sums. */
  amount: number;
  cash: boolean;
  reversalOf?: string;
}

export interface StockJournal extends JournalBase {
  kind: 'stock';
  productId: string;
  /** Signed, in base units. */
  delta: number;
  reason: StockReason;
}

export interface DayCloseJournal extends JournalBase {
  kind: 'dayclose';
  expected: number;
  counted: number | null;
  variance: number;
}

export type JournalEntry =
  | SaleJournal
  | PaymentJournal
  | PurchaseJournal
  | ExpenseJournal
  | StockJournal
  | DayCloseJournal;

/* ------------------------------------------------------------------ units -- */

/**
 * Converts a quantity in some unit to the product's base unit.
 *
 * Stock is held in the base unit, so this is the conversion that keeps a sale of
 * 0.5 ໂຕນ removing 10 bags rather than half a bag. `perBase` absent means 1,
 * which is right for the base unit and for single-unit products.
 */
export function toBaseUnits(product: Product, unit: string, qty: number): number {
  const def = product.units.find((u) => u.unit === unit);
  const perBase = def?.perBase ?? 1;
  return Math.round(qty * perBase * 100) / 100;
}

/* --------------------------------------------------------------- queries -- */

export function entriesIn(journal: JournalEntry[], range: DayRange): JournalEntry[] {
  return journal.filter((e) => inRange(e.day, range));
}

export interface SellerRow {
  productId: string;
  name: string;
  /** Quantity per unit, kept separate — see below. */
  byUnit: Record<string, number>;
  amount: number;
}

/**
 * Best-selling products over a period, by revenue.
 *
 * Quantities are kept per unit rather than summed. Adding 48 ຖົງ to 0.5 ໂຕນ
 * gives 48.5 of nothing; the display renders `48 ຖົງ · 0.5 ໂຕນ`, the aggregate
 * form of the same rule the cart badge already follows.
 */
export function topSellers(
  journal: JournalEntry[],
  range: DayRange,
  limit: number,
): SellerRow[] {
  const rows = new Map<string, SellerRow>();

  for (const entry of entriesIn(journal, range)) {
    if (entry.kind !== 'sale') continue;
    for (const line of entry.lines) {
      const row = rows.get(line.productId) ?? {
        productId: line.productId,
        name: line.name,
        byUnit: {},
        amount: 0,
      };
      row.byUnit[line.unit] = (row.byUnit[line.unit] ?? 0) + line.qty;
      row.amount += line.amount;
      rows.set(line.productId, row);
    }
  }

  return [...rows.values()].sort((a, b) => b.amount - a.amount).slice(0, limit);
}

/** "48 ຖົງ · 0.5 ໂຕນ" */
export function formatByUnit(byUnit: Record<string, number>): string {
  return Object.entries(byUnit)
    .map(([unit, qty]) => `${fmtQty(Math.round(qty * 100) / 100)} ${unit}`)
    .join(' · ');
}

/**
 * Cash that has physically left the till in a period.
 *
 * Cash expenses and cash purchases both come out of the drawer. Without this the
 * end-of-day drawer figure can never balance against a real count — and that
 * count is what the owner does every night.
 *
 * A purchase needs **both** flags. `cash` alone is not enough: a delivery taken
 * on credit (`paid: false`) has not moved any money yet, so counting it would
 * drop the drawer by cash that is still in it and leave the night's count short
 * with no visible reason. `paid` alone is not enough either — a bank transfer
 * settles the bill without touching the till.
 */
export function cashOut(journal: JournalEntry[], range: DayRange): number {
  return entriesIn(journal, range).reduce((sum, e) => {
    if (e.kind === 'expense' && e.cash) return sum + e.amount;
    if (e.kind === 'purchase' && e.paid && e.cash) return sum + e.total;
    return sum;
  }, 0);
}

export interface Movement {
  sold: number;
  received: number;
  adjusted: number;
}

/** What moved for one product within a range, in base units. */
export function movementsBetween(
  journal: JournalEntry[],
  productId: string,
  range: DayRange,
): Movement {
  const out: Movement = { sold: 0, received: 0, adjusted: 0 };

  for (const entry of entriesIn(journal, range)) {
    if (entry.kind === 'sale') {
      for (const l of entry.lines) if (l.productId === productId) out.sold += l.qtyBase;
    } else if (entry.kind === 'purchase') {
      for (const l of entry.lines) if (l.productId === productId) out.received += l.qtyBase;
    } else if (entry.kind === 'stock' && entry.productId === productId) {
      out.adjusted += entry.delta;
    }
  }

  out.sold = Math.round(out.sold * 100) / 100;
  out.received = Math.round(out.received * 100) / 100;
  out.adjusted = Math.round(out.adjusted * 100) / 100;
  return out;
}

/**
 * Stock at the end of a past day, by replaying backwards from today's figure.
 *
 * Replay rather than nightly snapshots: snapshots need a scheduler the app does
 * not have and a device that is awake at midnight — shops close the app — and a
 * snapshot taken from a wrong `remaining` freezes that error permanently.
 * Replay always reconciles to the current truth, so correcting today's count
 * corrects every historical reading with it.
 *
 * Products with `remaining: undefined` are not stock-tracked and stay undefined.
 */
export function stockAsOf(
  products: Product[],
  journal: JournalEntry[],
  day: DayKey,
): Map<string, number | undefined> {
  const out = new Map<string, number | undefined>();
  for (const p of products) out.set(p.id, p.remaining);

  const apply = (productId: string, delta: number) => {
    const current = out.get(productId);
    if (current === undefined) return; // not tracked
    out.set(productId, Math.round((current + delta) * 100) / 100);
  };

  // Everything after the target day is undone: a sale is added back, a purchase
  // taken away, an adjustment reversed.
  for (const entry of journal) {
    if (entry.day <= day) continue;
    if (entry.kind === 'sale') {
      for (const l of entry.lines) apply(l.productId, l.qtyBase);
    } else if (entry.kind === 'purchase') {
      for (const l of entry.lines) apply(l.productId, -l.qtyBase);
    } else if (entry.kind === 'stock') {
      apply(entry.productId, -entry.delta);
    }
  }

  return out;
}

/**
 * The last `days` days of takings, scaled against the busiest of them.
 *
 * For the hero sparkbar, oldest first, so the final bar is today. Each value is
 * 0–1 relative to the week's own peak rather than to an absolute target: a
 * quiet shop and a busy one both get a readable shape, and the bar is about the
 * trend rather than the amount.
 *
 * Returns an empty array when nothing was sold in the window. A row of zero-
 * height bars says "no trade" honestly; a row of invented ones does not.
 */
export function recentTrend(journal: JournalEntry[], today: DayKey, days = 7): number[] {
  const totals = new Map<DayKey, number>();
  for (const entry of journal) {
    if (entry.kind !== 'sale') continue;
    totals.set(entry.day, (totals.get(entry.day) ?? 0) + entry.total);
  }

  // `addDays` walks by local calendar day, which is the same arithmetic the
  // rest of the app uses. Building Dates here would reintroduce the UTC+7 trap.
  const window: number[] = [];
  for (let back = days - 1; back >= 0; back -= 1) {
    window.push(totals.get(addDays(today, -back)) ?? 0);
  }

  const peak = Math.max(...window);
  if (peak <= 0) return [];
  return window.map((value) => value / peak);
}
